import React, { useState, useEffect, useMemo } from 'react';
import {
    Users,
    Contact,
    FileText,
    ShieldCheck,
    Search,
    Loader2,
    RotateCcw,
    CheckCircle2,
    X,
    Filter,
    Info,
    Building,
    HeartPulse,
    CalendarDays,
    UserMinus,
    ChevronDown,
    Star,
    BookmarkPlus,
    Trash2,
    Layers,
    Table2
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import ReportRenderer from './ReportRenderer';
import DarkAwareSelect from './common/DarkAwareSelect';

const QUICK_SICIL_KEYS = ['personnel_list', 'personal_info', 'leave_balance_report'];

const QUICK_SICIL_LABELS = {
    personnel_list: 'Sicil Listesi',
    personal_info: 'Özlük Bilgileri',
    leave_balance_report: 'Yıllık İzin Bakiyeleri'
};

const PERSONNEL_REPORT_METADATA = {
    personnel_list: {
        id: 'personnel_list',
        name: 'Sicil Listesi Raporu',
        category: 'Sicil & İK',
        shortDesc: 'Kurumsal sicil numarası, departman, pozisyon, lokasyon, çalışma rejimi, işe giriş ve kıdem bilgileri.',
        icon: Users,
        accent: 'emerald',
        permissionKeys: ['reports:view_personnel_list']
    },
    personal_info: {
        id: 'personal_info',
        name: 'Özlük Bilgileri Raporu',
        category: 'Sicil & İK',
        shortDesc: 'T.C. Kimlik numarası, doğum tarihi, cinsiyet, kan grubu, iletişim ve acil durum kişi bilgileri.',
        icon: Contact,
        accent: 'amber',
        permissionKeys: ['reports:view_personal_info']
    },
    leave_balance_report: {
        id: 'leave_balance_report',
        name: 'Yıllık İzin Bakiye Raporu',
        category: 'Sicil & İK',
        shortDesc: 'Personellerin kıdem yılı, toplam hak edilen yıllık izin, kullanılan izin ve kalan izin bakiyeleri.',
        icon: CalendarDays,
        accent: 'teal',
        permissionKeys: ['reports:view_leave_balance_report']
    },
    terminated_employees: {
        id: 'terminated_employees',
        name: 'İşten Ayrılan Personeller Raporu',
        category: 'Sicil & İK',
        shortDesc: 'İşten ayrılan (pasif) personellerin işe giriş tarihi, işten çıkış tarihi ve toplam çalışma süresi.',
        icon: UserMinus,
        accent: 'rose',
        permissionKeys: ['reports:view_terminated_employees']
    },
    legal_consent_report: {
        id: 'legal_consent_report',
        name: 'KVKK / Aydınlatma Onay Raporu',
        category: 'Yasal & Uyum',
        shortDesc: 'Personellerin KVKK aydınlatma metni ve açık rıza onay durumları, versiyon ve onay tarihi.',
        icon: ShieldCheck,
        accent: 'teal',
        permissionKeys: ['reports:view_legal_consent_report']
    }
};

const ACCENT_STYLES = {
    emerald: {
        iconBox: 'bg-emerald-600 text-white',
        badge: 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700'
    },
    amber: {
        iconBox: 'bg-amber-600 text-white',
        badge: 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700'
    },
    teal: {
        iconBox: 'bg-teal-600 text-white',
        badge: 'bg-teal-50 text-teal-700 border-teal-300 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-700'
    },
    rose: {
        iconBox: 'bg-rose-600 text-white',
        badge: 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-700'
    }
};

const GENDER_OPTIONS = [
    { value: 'Erkek', label: 'Erkek' },
    { value: 'Kadın', label: 'Kadın' }
];

const BLOOD_GROUP_OPTIONS = [
    'A Rh+', 'A Rh-', 'B Rh+', 'B Rh-', 'AB Rh+', 'AB Rh-', '0 Rh+', '0 Rh-'
].map((bg) => ({ value: bg, label: bg }));

export default function PersonnelReportsPage() {
    const { firmCode, isAuthenticated, permissions = [] } = useAuth();

    const [selectedReport, setSelectedReport] = useState('personnel_list');
    const [viewMode, setViewMode] = useState('summary'); // 'summary' (Tek Satır Toplam) | 'detailed' (Yıl Bazlı Detay)
    const [includeSubtotals, setIncludeSubtotals] = useState(true);

    const presetStorageKey = `pdks_fav_sicil_reports_${firmCode || 'default'}`;
    const [savedPresets, setSavedPresets] = useState(() => {
        try {
            const raw = localStorage.getItem(`pdks_fav_sicil_reports_${firmCode || 'default'}`);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [showPresetInput, setShowPresetInput] = useState(false);
    const [presetNameInput, setPresetNameInput] = useState('');

    useEffect(() => {
        try {
            const raw = localStorage.getItem(presetStorageKey);
            setSavedPresets(raw ? JSON.parse(raw) : []);
        } catch {
            setSavedPresets([]);
        }
    }, [presetStorageKey]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedWorkingRegimes, setSelectedWorkingRegimes] = useState([]);
    const [selectedGenders, setSelectedGenders] = useState([]);
    const [selectedBloodGroups, setSelectedBloodGroups] = useState([]);

    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [locations, setLocations] = useState([]);
    const [workingRegimes, setWorkingRegimes] = useState([]);
    const [dropdownsLoading, setDropdownsLoading] = useState(true);
    const [dropdownsError, setDropdownsError] = useState(null);

    const [reportRequest, setReportRequest] = useState(null);
    const [catalogSicilReports, setCatalogSicilReports] = useState(null);

    useEffect(() => {
        let mounted = true;
        const loadCatalog = async () => {
            try {
                const res = await apiClient('Report/catalog');
                if (mounted && Array.isArray(res)) {
                    const sicilList = res.filter(
                        (r) =>
                            r.category === 'Sicil & İK Raporları' ||
                            [
                                'personnel_list',
                                'personal_info',
                                'leave_balance_report',
                                'terminated_employees',
                                'legal_consent_report'
                            ].includes(r.reportType)
                    );
                    setCatalogSicilReports(sicilList);
                }
            } catch (_) {
                if (mounted) {
                    setCatalogSicilReports(null);
                }
            }
        };
        if (isAuthenticated && firmCode) {
            loadCatalog();
        }
        return () => {
            mounted = false;
        };
    }, [isAuthenticated, firmCode, permissions]);

    const availableReports = useMemo(() => {
        if (Array.isArray(catalogSicilReports)) {
            return catalogSicilReports.map((item) => {
                const baseMeta = PERSONNEL_REPORT_METADATA[item.reportType] || {
                    id: item.reportType,
                    name: item.title || item.reportType,
                    category: item.category || 'Sicil & İK',
                    shortDesc: item.description || '',
                    icon: Users,
                    accent: 'emerald'
                };
                return {
                    ...baseMeta,
                    id: item.reportType,
                    name: item.title || baseMeta.name,
                    description: item.description || baseMeta.shortDesc,
                    shortDesc: item.description || baseMeta.shortDesc,
                    columns: item.columns || []
                };
            });
        }

        const list = Object.values(PERSONNEL_REPORT_METADATA).filter((meta) => {
            if (!Array.isArray(permissions) || permissions.length === 0) return false;
            return meta.permissionKeys.some((k) => permissions.includes(k));
        });
        return list.map((meta) => ({
            ...meta,
            description: meta.shortDesc
        }));
    }, [catalogSicilReports, permissions]);

    useEffect(() => {
        if (availableReports.length > 0 && !availableReports.some((r) => r.id === selectedReport)) {
            setSelectedReport(availableReports[0].id);
        }
    }, [availableReports, selectedReport]);

    const quickSicilReports = useMemo(() => {
        const list = [];
        for (const key of QUICK_SICIL_KEYS) {
            const found = availableReports.find((r) => r.id === key);
            if (found) list.push(found);
        }
        return list;
    }, [availableReports]);

    const otherSicilReports = useMemo(() => {
        return availableReports.filter((r) => !QUICK_SICIL_KEYS.includes(r.id));
    }, [availableReports]);

    const isOtherSicilSelected = useMemo(() => {
        return otherSicilReports.some((r) => r.id === selectedReport);
    }, [otherSicilReports, selectedReport]);

    const activeReportMeta = useMemo(
        () => availableReports.find((r) => r.id === selectedReport) || availableReports[0] || null,
        [availableReports, selectedReport]
    );

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!isAuthenticated || !firmCode) {
                setDropdownsError('Giriş yapılmamış veya firma kodu mevcut değil.');
                setDropdownsLoading(false);
                return;
            }
            setDropdownsLoading(true);
            setDropdownsError(null);
            try {
                const [empRes, deptRes, posRes, locRes, regimeRes] = await Promise.all([
                    apiClient('Employee/dropdown').catch(() => apiClient('Employee').catch(() => [])),
                    apiClient('Department').catch(() => []),
                    apiClient('Position').catch(() => []),
                    apiClient('Location').catch(() => []),
                    apiClient('WorkingRegime').catch(() => [])
                ]);

                setEmployees(
                    (empRes || []).map((e) => ({
                        value: e.id,
                        label: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim()
                    }))
                );
                setDepartments((deptRes || []).map((d) => ({ value: d.id, label: d.name })));
                setPositions((posRes || []).map((p) => ({ value: p.id, label: p.name })));
                setLocations((locRes || []).map((l) => ({ value: l.id, label: l.name })));
                setWorkingRegimes((regimeRes || []).map((r) => ({ value: r.id, label: r.name })));
            } catch (err) {
                console.error('Sicil raporu filtre verileri çekilirken hata:', err);
                setDropdownsError(`Filtreleme seçenekleri yüklenirken bir sorun oluştu: ${err.message}`);
            } finally {
                setDropdownsLoading(false);
            }
        };

        fetchInitialData();
    }, [firmCode, isAuthenticated]);

    const buildPayloadFilters = (overrideViewMode, overrideSubtotals) => {
        const now = new Date();
        return {
            startDate: new Date(now.getFullYear(), 0, 1),
            endDate: now,
            searchText: searchTerm.trim() ? searchTerm.trim() : null,
            personnelIds: selectedEmployees.length > 0 ? selectedEmployees.map((i) => i.value) : [],
            departmentIds: selectedDepartments.length > 0 ? selectedDepartments.map((i) => i.value) : [],
            positionIds: selectedPositions.length > 0 ? selectedPositions.map((i) => i.value) : [],
            locationIds: selectedLocations.length > 0 ? selectedLocations.map((i) => i.value) : [],
            workingRegimeIds: selectedWorkingRegimes.length > 0 ? selectedWorkingRegimes.map((i) => i.value) : [],
            genders: selectedGenders.length > 0 ? selectedGenders.map((i) => i.value) : [],
            bloodTypes: selectedBloodGroups.length > 0 ? selectedBloodGroups.map((i) => i.value) : [],
            viewMode: overrideViewMode !== undefined ? overrideViewMode : viewMode,
            includeSubtotals: overrideSubtotals !== undefined ? overrideSubtotals : includeSubtotals
        };
    };

    const handleGenerateReport = (overrideReportId) => {
        const targetReport = typeof overrideReportId === 'string' ? overrideReportId : selectedReport;
        if (!targetReport) return;
        setReportRequest({
            reportType: targetReport,
            filters: buildPayloadFilters(),
            key: Date.now()
        });
    };

    const handleSelectReport = (reportId) => {
        setSelectedReport(reportId);
        const defaultMode = reportId === 'leave_balance_report' ? 'summary' : 'detailed';
        setViewMode(defaultMode);
        if (reportRequest) {
            setReportRequest({
                reportType: reportId,
                filters: buildPayloadFilters(defaultMode, includeSubtotals),
                key: Date.now()
            });
        }
    };

    const handleChangeViewMode = (nextMode) => {
        setViewMode(nextMode);
        if (reportRequest && selectedReport) {
            setReportRequest({
                reportType: selectedReport,
                filters: buildPayloadFilters(nextMode, includeSubtotals),
                key: Date.now()
            });
        }
    };

    const handleToggleSubtotals = (nextVal) => {
        setIncludeSubtotals(nextVal);
        if (reportRequest && selectedReport) {
            setReportRequest({
                reportType: selectedReport,
                filters: buildPayloadFilters(viewMode, nextVal),
                key: Date.now()
            });
        }
    };

    const handleSavePreset = () => {
        const cleanName = presetNameInput.trim();
        if (!cleanName || !selectedReport) return;
        const newPreset = {
            id: `sicil_preset_${Date.now()}`,
            name: cleanName,
            reportType: selectedReport,
            viewMode,
            includeSubtotals,
            searchTerm,
            selectedEmployees,
            selectedDepartments,
            selectedPositions,
            selectedLocations,
            selectedWorkingRegimes,
            selectedGenders,
            selectedBloodGroups
        };
        const nextList = [newPreset, ...savedPresets.filter((p) => p.name !== cleanName)].slice(0, 12);
        setSavedPresets(nextList);
        try {
            localStorage.setItem(presetStorageKey, JSON.stringify(nextList));
        } catch {}
        setPresetNameInput('');
        setShowPresetInput(false);
    };

    const handleApplyPreset = (preset) => {
        if (!preset) return;
        const nextRep = preset.reportType || selectedReport;
        const nextMode = preset.viewMode || 'summary';
        const nextSubs = preset.includeSubtotals !== undefined ? preset.includeSubtotals : true;

        setSelectedReport(nextRep);
        setViewMode(nextMode);
        setIncludeSubtotals(nextSubs);
        setSearchTerm(preset.searchTerm || '');
        setSelectedEmployees(preset.selectedEmployees || []);
        setSelectedDepartments(preset.selectedDepartments || []);
        setSelectedPositions(preset.selectedPositions || []);
        setSelectedLocations(preset.selectedLocations || []);
        setSelectedWorkingRegimes(preset.selectedWorkingRegimes || []);
        setSelectedGenders(preset.selectedGenders || []);
        setSelectedBloodGroups(preset.selectedBloodGroups || []);

        const now = new Date();
        setReportRequest({
            reportType: nextRep,
            filters: {
                startDate: new Date(now.getFullYear(), 0, 1),
                endDate: now,
                searchText: (preset.searchTerm || '').trim() || null,
                personnelIds: (preset.selectedEmployees || []).map((i) => i.value),
                departmentIds: (preset.selectedDepartments || []).map((i) => i.value),
                positionIds: (preset.selectedPositions || []).map((i) => i.value),
                locationIds: (preset.selectedLocations || []).map((i) => i.value),
                workingRegimeIds: (preset.selectedWorkingRegimes || []).map((i) => i.value),
                genders: (preset.selectedGenders || []).map((i) => i.value),
                bloodTypes: (preset.selectedBloodGroups || []).map((i) => i.value),
                viewMode: nextMode,
                includeSubtotals: nextSubs
            },
            key: Date.now()
        });
    };

    const handleDeletePreset = (id, e) => {
        e?.stopPropagation?.();
        const nextList = savedPresets.filter((p) => p.id !== id);
        setSavedPresets(nextList);
        try {
            localStorage.setItem(presetStorageKey, JSON.stringify(nextList));
        } catch {}
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedEmployees([]);
        setSelectedDepartments([]);
        setSelectedPositions([]);
        setSelectedLocations([]);
        setSelectedWorkingRegimes([]);
        setSelectedGenders([]);
        setSelectedBloodGroups([]);
        setReportRequest(null);
    };

    const activeFilterCount =
        (searchTerm.trim() ? 1 : 0) +
        (selectedEmployees.length > 0 ? 1 : 0) +
        (selectedDepartments.length > 0 ? 1 : 0) +
        (selectedPositions.length > 0 ? 1 : 0) +
        (selectedLocations.length > 0 ? 1 : 0) +
        (selectedWorkingRegimes.length > 0 ? 1 : 0) +
        (selectedGenders.length > 0 ? 1 : 0) +
        (selectedBloodGroups.length > 0 ? 1 : 0);

    const FilterLabel = ({ children }) => (
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            {children}
        </label>
    );

    return (
        <div className="w-full space-y-6">
            <div className="w-full bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700 space-y-6">
                {/* ÜST BAŞLIK */}
                <div className="flex flex-wrap justify-between items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2.5">
                            <Users className="w-6 h-6 text-emerald-500" />
                            Sicil ve Özlük Raporları
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Personel sicil listesini, iletişim/özlük detaylarını ve KVKK onay durumlarını filtreleyerek görüntüleyin veya Excel/PDF olarak dışa aktarın.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeFilterCount > 0 && (
                            <span className="px-3 py-1 text-xs font-bold rounded-full bg-amber-50 text-amber-700 border border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700">
                                {activeFilterCount} Aktif Filtre
                            </span>
                        )}
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700">
                            {availableReports.length} Rapor Türü
                        </span>
                    </div>
                </div>

                {/* FAVORİ SİCİL RAPOR ŞABLONLARI BARI */}
                <div className="flex flex-wrap items-center justify-between gap-2.5 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 mr-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
                            Favori Raporlarım:
                        </span>
                        {savedPresets.length === 0 ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                Sık kullandığınız sicil/izin bakiye raporu ve filtre seçimlerinizi kaydedip tek tıkla çalıştırabilirsiniz.
                            </span>
                        ) : (
                            savedPresets.map((preset) => (
                                <div
                                    key={preset.id}
                                    onClick={() => handleApplyPreset(preset)}
                                    className="group inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 hover:border-emerald-500 dark:hover:border-emerald-500 text-gray-800 dark:text-gray-200 cursor-pointer shadow-2xs transition-all"
                                    title="Bu favori raporu yükle ve çalıştır"
                                >
                                    <Star className="w-3 h-3 text-amber-500 fill-amber-400 shrink-0" />
                                    <span>{preset.name}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDeletePreset(preset.id, e)}
                                        className="ml-0.5 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400"
                                        title="Favoriyi Sil"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {showPresetInput ? (
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="text"
                                    value={presetNameInput}
                                    onChange={(e) => setPresetNameInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                                    placeholder="Örn: İzin Bakiye Tek Satır..."
                                    className="px-2.5 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 w-52"
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={handleSavePreset}
                                    className="px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                    Kaydet
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPresetInput(false);
                                        setPresetNameInput('');
                                    }}
                                    className="px-2 py-1 rounded-lg text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                >
                                    İptal
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    const defaultLabel = activeReportMeta
                                        ? activeReportMeta.name
                                        : 'Favori Sicil Raporum';
                                    setPresetNameInput(defaultLabel);
                                    setShowPresetInput(true);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-700 transition-all"
                            >
                                <BookmarkPlus className="w-3.5 h-3.5" />
                                Mevcut Seçimi Favorilere Ekle
                            </button>
                        )}
                    </div>
                </div>

                {/* 1. RAPOR SEÇİM ALANI: HIZLI ERİŞİM BUTONLARI + DİĞER SİCİL RAPORLARI LİSTESİ */}
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                            1. Görüntülenecek Rapor Türünü Seçin
                        </div>
                        {otherSicilReports.length > 0 && (
                            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
                                Hızlı erişim butonlarından veya sağdaki açılır listeden rapor seçebilirsiniz
                            </span>
                        )}
                    </div>

                    {availableReports.length === 0 ? (
                        <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
                            Hesabınıza tanımlı görüntülenebilir Sicil & İK raporu bulunmamaktadır.
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
                                {quickSicilReports.length > 0 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 flex-1">
                                        {quickSicilReports.map((report) => {
                                            const IconComponent = report.icon || FileText;
                                            const isSelected = selectedReport === report.id;
                                            const style =
                                                ACCENT_STYLES[report.accent] || ACCENT_STYLES.emerald;
                                            const shortLabel =
                                                QUICK_SICIL_LABELS[report.id] || report.name;

                                            return (
                                                <button
                                                    key={report.id}
                                                    type="button"
                                                    onClick={() => handleSelectReport(report.id)}
                                                    className={`group relative rounded-xl px-3.5 py-2.5 border transition-all duration-150 flex items-center gap-2.5 text-left ${
                                                        isSelected
                                                            ? 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-500 ring-1 ring-emerald-500/40 shadow-sm'
                                                            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                                                    }`}
                                                >
                                                    <div
                                                        className={`w-8 h-8 rounded-lg ${style.iconBox} flex items-center justify-center shadow-xs shrink-0`}
                                                    >
                                                        <IconComponent className="w-4 h-4" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-xs font-bold text-gray-900 dark:text-white truncate">
                                                            {shortLabel}
                                                        </div>
                                                        <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                                            {isSelected ? 'Aktif Rapor' : 'Hızlı Seçim'}
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}

                                {otherSicilReports.length > 0 && (
                                    <div className="lg:w-80 xl:w-96 shrink-0">
                                        <div
                                            className={`relative rounded-xl border transition-all duration-150 ${
                                                isOtherSicilSelected
                                                    ? 'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-500 ring-1 ring-emerald-500/40 shadow-sm'
                                                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                                            }`}
                                        >
                                            <div className="px-3 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center justify-between">
                                                <span>
                                                    Diğer Sicil & İK Raporları ({otherSicilReports.length})
                                                </span>
                                                {isOtherSicilSelected && (
                                                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                                        <CheckCircle2 className="w-3 h-3" /> Listeden Seçili
                                                    </span>
                                                )}
                                            </div>
                                            <div className="relative">
                                                <select
                                                    value={isOtherSicilSelected ? selectedReport : ''}
                                                    onChange={(e) => {
                                                        if (e.target.value) {
                                                            handleSelectReport(e.target.value);
                                                        }
                                                    }}
                                                    className="w-full appearance-none bg-transparent pl-3 pr-9 pb-2 pt-0.5 text-xs font-bold text-gray-900 dark:text-white focus:outline-none cursor-pointer"
                                                >
                                                    <option
                                                        value=""
                                                        className="bg-white dark:bg-gray-900 text-gray-500"
                                                    >
                                                        Listeden diğer bir rapor seçin...
                                                    </option>
                                                    {otherSicilReports.map((rep) => (
                                                        <option
                                                            key={rep.id}
                                                            value={rep.id}
                                                            className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white py-1"
                                                        >
                                                            {rep.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {activeReportMeta && (
                                <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700">
                                    <div className="flex items-center gap-3 min-w-0">
                                        {(() => {
                                            const activeStyle =
                                                ACCENT_STYLES[activeReportMeta.accent] ||
                                                ACCENT_STYLES.emerald;
                                            const ActiveIcon = activeReportMeta.icon || FileText;
                                            return (
                                                <div
                                                    className={`w-8 h-8 rounded-lg ${activeStyle.iconBox} flex items-center justify-center shrink-0`}
                                                >
                                                    <ActiveIcon className="w-4 h-4" />
                                                </div>
                                            );
                                        })()}
                                        <div className="min-w-0">
                                            <div className="text-xs font-bold text-gray-900 dark:text-white">
                                                {activeReportMeta.name}
                                            </div>
                                            <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                                                {activeReportMeta.description}
                                            </div>
                                        </div>
                                    </div>

                                    {activeReportMeta.id === 'leave_balance_report' ? (
                                        <div className="flex flex-wrap items-center gap-2.5 shrink-0 pt-2 xl:pt-0 border-t xl:border-t-0 border-gray-200 dark:border-gray-700">
                                            <div className="inline-flex items-center rounded-xl p-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 shadow-2xs">
                                                <button
                                                    type="button"
                                                    onClick={() => handleChangeViewMode('summary')}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                        viewMode === 'summary'
                                                            ? 'bg-emerald-600 text-white shadow-xs'
                                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                                    }`}
                                                >
                                                    <Table2 className="w-3.5 h-3.5" />
                                                    Personel Bazlı Tek Satır Toplam
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleChangeViewMode('detailed')}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                        viewMode === 'detailed'
                                                            ? 'bg-emerald-600 text-white shadow-xs'
                                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                                    }`}
                                                >
                                                    <Layers className="w-3.5 h-3.5" />
                                                    Hakediş Yılı Bazlı Detay
                                                </button>
                                            </div>

                                            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-700 dark:text-gray-200 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={includeSubtotals}
                                                    onChange={(e) => handleToggleSubtotals(e.target.checked)}
                                                    className="rounded text-emerald-600 focus:ring-emerald-500 w-3.5 h-3.5"
                                                />
                                                <span>∑ Toplam Satırı Göster</span>
                                            </label>
                                        </div>
                                    ) : (
                                        <span className="px-2.5 py-0.5 rounded-md text-[11px] font-bold bg-emerald-100/80 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 shrink-0 self-start xl:self-center">
                                            Seçili Şablon
                                        </span>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* 2. FİLTRELEME SEÇENEKLERİ */}
                <div className="bg-gray-50 dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 space-y-5">
                    <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200 flex items-center gap-2">
                            <Filter className="w-4 h-4 text-emerald-500" />
                            2. Filtreleme Seçenekleri
                        </span>
                    </div>

                    {dropdownsLoading ? (
                        <div className="flex items-center justify-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                            <Loader2 className="w-5 h-5 animate-spin mr-2 text-emerald-500" />
                            Filtreleme seçenekleri yükleniyor...
                        </div>
                    ) : dropdownsError ? (
                        <div className="p-3.5 rounded-xl bg-red-50 dark:bg-red-900/40 border border-red-300 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
                            {dropdownsError}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Serbest Arama */}
                            <div>
                                <FilterLabel>Serbest Arama (Ad, Sicil No, T.C.)</FilterLabel>
                                <div className="relative">
                                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleGenerateReport()}
                                        placeholder="Ad soyad, sicil veya T.C. Kimlik..."
                                        className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-emerald-500 outline-none"
                                    />
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchTerm('')}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Personel */}
                            <div>
                                <FilterLabel>Personel Seçimi</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={employees}
                                    value={selectedEmployees}
                                    onChange={(val) => setSelectedEmployees(val || [])}
                                    placeholder="Tüm Personeller..."
                                />
                            </div>

                            {/* Departman */}
                            <div>
                                <FilterLabel>Departman / Bölüm</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={departments}
                                    value={selectedDepartments}
                                    onChange={(val) => setSelectedDepartments(val || [])}
                                    placeholder="Tüm Departmanlar..."
                                />
                            </div>

                            {/* Pozisyon */}
                            <div>
                                <FilterLabel>Pozisyon / Unvan</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={positions}
                                    value={selectedPositions}
                                    onChange={(val) => setSelectedPositions(val || [])}
                                    placeholder="Tüm Pozisyonlar..."
                                />
                            </div>

                            {/* Lokasyon */}
                            <div>
                                <FilterLabel>Çalışma Lokasyonu</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={locations}
                                    value={selectedLocations}
                                    onChange={(val) => setSelectedLocations(val || [])}
                                    placeholder="Tüm Lokasyonlar..."
                                />
                            </div>

                            {/* Çalışma Rejimi */}
                            <div>
                                <FilterLabel>Çalışma Rejimi</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={workingRegimes}
                                    value={selectedWorkingRegimes}
                                    onChange={(val) => setSelectedWorkingRegimes(val || [])}
                                    placeholder="Tüm Çalışma Rejimleri..."
                                />
                            </div>

                            {/* Cinsiyet */}
                            <div>
                                <FilterLabel>Cinsiyet</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={GENDER_OPTIONS}
                                    value={selectedGenders}
                                    onChange={(val) => setSelectedGenders(val || [])}
                                    placeholder="Tümü..."
                                />
                            </div>

                            {/* Kan Grubu */}
                            <div>
                                <FilterLabel>Kan Grubu</FilterLabel>
                                <DarkAwareSelect
                                    isMulti
                                    options={BLOOD_GROUP_OPTIONS}
                                    value={selectedBloodGroups}
                                    onChange={(val) => setSelectedBloodGroups(val || [])}
                                    placeholder="Tüm Kan Grupları..."
                                />
                            </div>
                        </div>
                    )}

                    {/* Alt Aksiyon Barı */}
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                            <Info className="w-4 h-4 mr-1.5 text-emerald-500 shrink-0" />
                            {activeReportMeta
                                ? `"${activeReportMeta.name}" raporu seçili kriterlere göre hazırlanacaktır.`
                                : 'Rapor oluşturmak için yukarıdan bir rapor kartı seçiniz.'}
                        </div>

                        <div className="flex w-full sm:w-auto gap-3">
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                className="flex-1 sm:flex-none inline-flex items-center justify-center px-5 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl shadow-sm hover:bg-gray-100 transition-all text-sm font-semibold"
                            >
                                <RotateCcw className="w-4 h-4 mr-1.5" />
                                Filtreleri Sıfırla
                            </button>
                            <button
                                type="button"
                                onClick={() => handleGenerateReport()}
                                disabled={dropdownsLoading || !selectedReport}
                                className="flex-1 sm:flex-none inline-flex items-center justify-center px-7 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm transition-all text-sm font-bold disabled:opacity-50"
                            >
                                <Filter className="w-4 h-4 mr-2" />
                                Raporu Oluştur
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 3. RAPOR SONUÇ TABLOSU */}
            {reportRequest && (
                <ReportRenderer
                    request={reportRequest}
                    hideDateBadge={true}
                />
            )}
        </div>
    );
}
